import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, DownloadIcon, ExternalLinkIcon, FileTextIcon } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { dictionary } from '../lib/content';
export function Papers() {
  const {
    lang,
    t
  } = useI18n();
  const items = dictionary[lang].papers.items;
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const allTags = useMemo(() => {
    const set = new Set<string>();
    items.forEach(p => p.tags.forEach(tag => set.add(tag)));
    return Array.from(set);
  }, [items]);
  const filtered = items.filter(p => {
    const matchesQuery = p.title.toLowerCase().includes(query.toLowerCase());
    const matchesTag = !activeTag || p.tags.includes(activeTag);
    return matchesQuery && matchesTag;
  });
  return <section className="w-full bg-[#05060A] py-32 px-6 md:px-12 border-t border-white/5" data-id="element-150">
      <div className="max-w-6xl mx-auto" data-id="element-151">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-4xl md:text-5xl font-light text-white mb-12 tracking-tight" data-id="element-152">
          {t('papers.title')}
        </motion.h2>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10" data-id="element-153">
          <div className="relative flex-1 max-w-md" data-id="element-154">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" aria-hidden="true" data-id="element-155" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={t('papers.search')} className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:border-violet-500/50 transition" data-id="element-156" />
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-157">
            {allTags.map(tag => <button key={tag} onClick={() => setActiveTag(activeTag === tag ? null : tag)} className={`px-3 py-1.5 text-xs rounded-full border transition ${activeTag === tag ? 'bg-violet-500/20 border-violet-500/50 text-violet-200' : 'border-white/10 text-white/60 hover:border-white/30 hover:text-white/80'}`} data-id="element-158">
                {tag}
              </button>)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-159">
          {filtered.map((paper, i) => <motion.article key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} className="group p-7 bg-white/[0.02] border border-white/10 rounded-xl hover:border-violet-500/40 hover:bg-white/[0.04] transition" data-id="element-160">
              <div className="flex items-start justify-between mb-4" data-id="element-161">
                <FileTextIcon className="w-5 h-5 text-violet-400" aria-hidden="true" data-id="element-162" />
                <span className="text-xs font-mono text-white/40" data-id="element-163">
                  {paper.date}
                </span>
              </div>
              <h3 className="text-lg text-white font-light leading-snug mb-3" data-id="element-164">
                {paper.title}
              </h3>
              <p className="text-xs text-white/50 mb-5" data-id="element-165">{paper.authors}</p>
              <div className="flex flex-wrap gap-2 mb-6" data-id="element-166">
                {paper.tags.map(tag => <span key={tag} className="px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/50 border border-white/10 rounded" data-id="element-167">
                    {tag}
                  </span>)}
              </div>
              <div className="flex gap-3 pt-4 border-t border-white/5" data-id="element-168">
                <button className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-violet-300 transition" data-id="element-169">
                  <DownloadIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-170" />
                  {t('papers.download')}
                </button>
                <button className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-violet-300 transition" data-id="element-171">
                  <ExternalLinkIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-172" />
                  {t('papers.viewOnline')}
                </button>
              </div>
            </motion.article>)}
        </div>

        {filtered.length === 0 && <p className="text-center text-white/40 py-12 text-sm" data-id="element-173">No results.</p>}
      </div>
    </section>;
}